import create from 'zustand';
import { useEffect, useCallback, useRef, useState } from 'react';
import { useUIState, useRequireAuth } from 'components';
import Client, { TOrderIssues, TShopifyCreatedOrder, TDashboardSnapshot } from './api-client';
import { ICategoryBreakdown } from 'type';
import moment from 'moment';

interface OldestOpenIssue {
  issue: any;
  order: any;
}

interface IDashboardStore {
  orders: Array<TShopifyCreatedOrder>;
  totalOrders: number;
  totalOrdersToday: number;
  issues: Array<TOrderIssues>;
  totalOpenIssues: number;
  totalIssuesToday: number;
  snapshots: Array<TDashboardSnapshot>;
  productCategories: ICategoryBreakdown[];
  oldestOpenIssue: OldestOpenIssue | null;
  ordersAtRiskCount: number;
  issuesByDriver: Array<{
    driverReference: string;
    driverName: string;
    totalOrders: number;
    ordersWithIssues: number;
    issueFreeRate: number;
  }>;
  movingVehiclesCount: number;
  totalStopsRemaining: number;
}

export const useDashboardStore = create<IDashboardStore>((set, get) => ({
  orders: [],
  totalOrders: 0,
  totalOrdersToday: 0,
  issues: [],
  totalOpenIssues: 0,
  totalIssuesToday: 0,
  snapshots: [],
  productCategories: [],
  oldestOpenIssue: null,
  ordersAtRiskCount: 0,
  issuesByDriver: [],
  movingVehiclesCount: 0,
  totalStopsRemaining: 0,
}));

export const useFetchProductCategories = () => {
  const { isLoggedIn } = useRequireAuth();
  const [productCategoriesLoading, setProductCategoriesLoading] = useState(false);
  const [productCategoriesError, setProductCategoriesError] = useState<string | null>(null);
  const lastProductCategoriesFetchTimeRef = useRef<number | null>(null);

  const fetchProductCategories = useCallback(async () => {
    if (!isLoggedIn) {
      return;
    }

    setProductCategoriesLoading(true);
    setProductCategoriesError(null);

    try {
      const postingDateFrom = moment().subtract(30, 'days').format('YYYY-MM-DD');
      const postingDateTo = moment().format('YYYY-MM-DD');

      const res = await Client.Api.Operations.productCategoriesBreakdown({
        postingDateFrom,
        postingDateTo,
        categoryType: 'product',
      });

      if (res.data.success && res.data.data?.categoriesBreakdown) {
        const breakdown: ICategoryBreakdown[] = res.data.data.categoriesBreakdown;
        useDashboardStore.setState({
          productCategories: breakdown,
        });
        setProductCategoriesLoading(false);
        setProductCategoriesError(null);
        lastProductCategoriesFetchTimeRef.current = Date.now();
      } else {
        useDashboardStore.setState({
          productCategories: [],
        });
        setProductCategoriesLoading(false);
        setProductCategoriesError('Failed to fetch category data');
      }
    } catch (err) {
      console.error('Error fetching product categories:', err);
      useDashboardStore.setState({
        productCategories: [],
      });
      setProductCategoriesLoading(false);
      setProductCategoriesError('Error loading data');
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    const checkAndFetchProductCategories = () => {
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;
      
      if (
        !lastProductCategoriesFetchTimeRef.current ||
        now - lastProductCategoriesFetchTimeRef.current >= fiveMinutes
      ) {
        fetchProductCategories();
      }
    };

    checkAndFetchProductCategories();

    const interval = setInterval(checkAndFetchProductCategories, 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchProductCategories, isLoggedIn]);

  return {
    productCategoriesLoading,
    productCategoriesError,
    refreshProductCategories: fetchProductCategories,
  };
};

export const useDashboardData = () => {
  const [uiState, setUI] = useUIState();
  const { isLoggedIn } = useRequireAuth();
  const isFetchingRef = useRef(false);
  const hasInitialFetchRef = useRef(false);
  const lastSnapshotFetchDateRef = useRef<string | null>(null);
  const prevLoggedInRef = useRef<boolean>(false);
  const setUIRef = useRef(setUI);

  useEffect(() => {
    setUIRef.current = setUI;
  }, [setUI]);

  const fetchSnapshots = useCallback(async () => {
    try {
      const endDate = moment().format('YYYY-MM-DD');
      const startDate = moment().subtract(30, 'days').format('YYYY-MM-DD');

      const snapshotsRes = await Client.Api.Shopify.listDashboardSnapshots({
        startDate,
        endDate,
        orderBy: 'snapshotDate',
      });

      if (snapshotsRes.data.error) {
        console.error('Error fetching snapshots:', snapshotsRes.data.error);
        return;
      }

      const snapshots = snapshotsRes.data.data?.snapshots || [];
      useDashboardStore.setState({ snapshots });
      lastSnapshotFetchDateRef.current = endDate;
    } catch (err) {
      console.error('Error fetching snapshots:', err);
    }
  }, []);

  const fetchOrdersAndIssues = useCallback(async () => {
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    setUIRef.current({ fetching: true });
    try {
      const [ordersRes, issuesRes, dashboardMetricsRes] = await Promise.all([
        Client.Api.Shopify.listShopifyOrder({
          limit: 30,
          offset: 0,
          orderBy: 'updatedAt',
          liveDeliveryOnly: '1',
        }),
        Client.Api.Shopify.listOrderIssues({
          limit: 1000,
          offset: 0,
          orderBy: 'createdAt',
          // details: true,
        }),
        Client.Api.Shopify.getDashboardMetrics(),
      ]);

      if (ordersRes.data.error) {
        setUIRef.current({ fetching: false, errorMes: ordersRes.data.error });
        isFetchingRef.current = false;
        return;
      }

      if (issuesRes.data.error) {
        setUIRef.current({ fetching: false, errorMes: issuesRes.data.error });
        isFetchingRef.current = false;
        return;
      }

      const orders = ordersRes.data.data?.orders || [];
      const issues = issuesRes.data.data?.issues || [];
      const totalOrders = ordersRes.data.data?.total || 0;
      const totalOpenIssues = issues.filter((issue: TOrderIssues) => issue.status === 'pending')?.length || 0;

      const today = moment().startOf('day');
      
      const totalOrdersToday = dashboardMetricsRes?.data?.success 
        ? (dashboardMetricsRes.data.data?.totalOrdersToday ?? 0)
        : orders.filter((order: any) => {
            const orderDate = moment(order.createdAt || order.rawData?.createdAt);
            return orderDate.isSameOrAfter(today) && orderDate.isBefore(moment(today).add(1, 'day'));
          }).length;

      const totalIssuesToday = issues.filter((issue: TOrderIssues) => {
        const issueDate = moment(issue.createdAt);
        return issueDate.isSameOrAfter(today) && issueDate.isBefore(moment(today).add(1, 'day'));
      }).length;

      const oldestOpenIssue = dashboardMetricsRes?.data?.success 
        ? (dashboardMetricsRes.data.data?.oldestOpenIssue || null)
        : null;

      const ordersAtRiskCount = dashboardMetricsRes?.data?.success 
        ? (dashboardMetricsRes.data.data?.ordersAtRiskCount || 0)
        : 0;

      const issuesByDriver = dashboardMetricsRes?.data?.success 
        ? (dashboardMetricsRes.data.data?.issuesByDriver || [])
        : [];

      const movingVehiclesCount = dashboardMetricsRes?.data?.success 
        ? (dashboardMetricsRes.data.data?.movingVehiclesCount || 0)
        : 0;

      const totalStopsRemaining = dashboardMetricsRes?.data?.success 
        ? (dashboardMetricsRes.data.data?.totalStopsRemaining || 0)
        : 0;

      useDashboardStore.setState({
        orders,
        totalOrders,
        totalOrdersToday,
        issues,
        totalOpenIssues,
        totalIssuesToday,
        oldestOpenIssue,
        ordersAtRiskCount,
        issuesByDriver,
        movingVehiclesCount,
        totalStopsRemaining,
      });

      setUIRef.current({ fetching: false, errorMes: '' });
    } catch (err) {
      setUIRef.current({ fetching: false, errorMes: String(err) });
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  const fetchData = useCallback(async () => {
    await Promise.all([fetchOrdersAndIssues(), fetchSnapshots()]);
  }, [fetchOrdersAndIssues, fetchSnapshots]);

  useEffect(() => {
    if (!hasInitialFetchRef.current) {
      hasInitialFetchRef.current = true;
      fetchData();
    }
  }, []);

  // Daily snapshot refresh - check if we need to fetch snapshots (once per day)
  useEffect(() => {
    const checkAndFetchSnapshots = () => {
      const today = moment().format('YYYY-MM-DD');
      if (lastSnapshotFetchDateRef.current !== today) {
        fetchSnapshots();
      }
    };

    checkAndFetchSnapshots();

    // Check every minute to see if we need to fetch new snapshots
    const interval = setInterval(checkAndFetchSnapshots, 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchSnapshots]);

  useEffect(() => {
    if (isLoggedIn && !prevLoggedInRef.current) {
      fetchOrdersAndIssues();
      fetchSnapshots();
    }
    prevLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn, fetchOrdersAndIssues, fetchSnapshots]);

  useEffect(() => {
    if (!isLoggedIn) return;
    
    const interval = setInterval(() => {
      fetchOrdersAndIssues();
    }, 30000);
    return () => clearInterval(interval);
  }, [isLoggedIn, fetchOrdersAndIssues]);

  return {
    uiState,
    refresh: fetchOrdersAndIssues,
    refreshSnapshots: fetchSnapshots,
  };
}
